import { Component, Inject, OnDestroy, OnInit } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { environment } from 'src/environments/environment';
import { RegistreService } from '../registre.service';
import { NotificationService } from 'src/app/services/notification.service';
import { AuthService } from 'src/app/services/auth.service';
import { catchError, distinctUntilChanged, finalize, map, Observable, of, Subject, switchMap, takeUntil, tap } from 'rxjs';
import { helper } from 'src/environments/helper';
import { Generic } from 'src/app/interfaces/generic.interface';
import { CommonService } from 'src/app/services/common.service';

@Component({
    selector: 'app-edit-registre',
    templateUrl: './edit-registre.component.html',
    styleUrls: ['./edit-registre.component.scss']
})
export class EditRegistreComponent implements OnInit, OnDestroy {

    // general error
    errorTitle: string = environment.config.customNotifications.headers.error
    errorIcon: string = environment.config.customNotifications.icons.error
    errorType: string = environment.config.customNotifications.icons.error
    helper = helper;

    token!: string;
    isLoading = false;
    // serieType$: any = [];

    directii: Generic[] = [];
    filteredDirectii: Generic[] = [];
    directiiControl = new FormControl();

    compartimente: Generic[] = [];
    filteredCompartimente: Generic[] = [];
    compartimenteControl = new FormControl();

    registre: any[] = [];
    filteredRegistre: Generic[] = [];
    registreControl = new FormControl();

    categorii: any[] = [];

    documente: any[] = [];
    filteredDocumente: any[] = [];
    documenteControl = new FormControl();

    sources: any[] = [];
    filteredSources: any[] = [];
    sourcesControl = new FormControl();

    private editDestroy$ = new Subject<void>();

    defaultParameter: { name: string; id: string } = {
        name: '--Alegeți--',
        id: '-1',
    };
    serieType: { id: string, name: string }[] = [
        this.defaultParameter,
        {
            name: "Resetabilă anual",
            id: '1',
        },
        {
            name: "Numărătoare continuă",
            id: '2',
        },
    ];

    form: FormGroup = new FormGroup({
        name: new FormControl(this.data.name, [Validators.required]),
        slug: new FormControl(this.data.slug, [Validators.required]),
        serie_type: new FormControl(this.data.serie_type.toString(), [Validators.required, Validators.min(1)]),
        indoc_directie_id: new FormControl(this.data.indoc_directie_id),
        indoc_directie_name: new FormControl(this.data.indoc_directie_name),
        indoc_compartiment_id: new FormControl(this.data.indoc_compartiment_id),
        indoc_compartiment_name: new FormControl(this.data.indoc_compartiment_name),
        indoc_registru_id: new FormControl(this.data.indoc_registru_id),
        indoc_registru_name: new FormControl(this.data.indoc_registru_name),
        indoc_categorie_id: new FormControl(this.data.indoc_categorie_id),
        indoc_categorie_name: new FormControl(this.data.indoc_categorie_name),
        indoc_document_type_id: new FormControl(this.data.indoc_document_type_id),
        indoc_document_type_name: new FormControl(this.data.indoc_document_type_name),
        indoc_source_id: new FormControl(this.data.indoc_source_id),
        indoc_source_name: new FormControl(this.data.indoc_source_name),
        observatii: new FormControl(this.data.observatii),
    });

    constructor(
        @Inject(MAT_DIALOG_DATA)
        public data: any,
        private dialogRef: MatDialogRef<EditRegistreComponent>,
        private registreService: RegistreService,
        private notificationService: NotificationService,
        private authService: AuthService,
        private commonService: CommonService
    ) {
        this.processToken();
    }

    ngOnInit(): void {
        this.loadDirectiiActive();
        this.renderFieldsSearch();
        this.loadRegistre();
        this.loadCategorii();
        this.loadDocumente();
        this.loadSources();

        if (this.data.indoc_directie_id) {
            this.loadCompartimenteActive(this.data.indoc_directie_id)
        }
    }

    ngOnDestroy(): void {
        this.editDestroy$.next();
        this.editDestroy$.complete();
    }

    closeDialog() {
        this.dialogRef?.close({
            success: false,
        });
    }

    processToken() {
        const tokenObj = this.authService.returnTokenUser();
        tokenObj.then((token) => {
            this.token = token;
            this.registreService.setToken(token);
        });
    }

    renderFieldsSearch() {
        this.directiiControl.valueChanges.pipe(
            takeUntil(this.editDestroy$),
            distinctUntilChanged(),
            switchMap((inputValue) => {
                if (inputValue.length < 1) {
                    this.filteredDirectii = this.directii;
                    return of(this.directii);
                }
                return this.filterDirectii(inputValue);
            }),
            tap((data: any[]) => {
                this.filteredDirectii = data;
            }),
            catchError(() => of([]))
        ).subscribe();

        this.compartimenteControl.valueChanges.pipe(
            takeUntil(this.editDestroy$),
            distinctUntilChanged(),
            switchMap((inputValue) => {
                if (inputValue.length < 1) {
                    this.filteredCompartimente = this.compartimente;
                    return of(this.compartimente);
                }
                return this.filterCompartimente(inputValue);
            }),
            tap((data: any[]) => {
                this.filteredCompartimente = data;
            }),
            catchError(() => of([]))
        ).subscribe();

        this.documenteControl.valueChanges.pipe(
            takeUntil(this.editDestroy$),
            distinctUntilChanged(),
            switchMap((inputValue) => {
                if (inputValue.length < 1) {
                    this.filteredDocumente = this.documente;
                    return of(this.documente);
                }
                return this.filterDocumente(inputValue);
            }),
            tap((data: any[]) => {
                this.filteredDocumente = data;
            }),
            catchError(() => of([]))
        ).subscribe();

        this.registreControl.valueChanges.pipe(
            takeUntil(this.editDestroy$),
            distinctUntilChanged(),
            switchMap((inputValue) => {
                if (inputValue.length < 1) {
                    this.filteredRegistre = this.registre;
                    return of(this.registre);
                }
                return this.filterRegistre(inputValue);
            }),
            tap((data: any[]) => {
                this.filteredRegistre = data;
            }),
            catchError(() => of([]))
        ).subscribe();

        this.sourcesControl.valueChanges.pipe(
            takeUntil(this.editDestroy$),
            distinctUntilChanged(),
            switchMap((inputValue) => {
                if (inputValue.length < 1) {
                    this.filteredSources = this.sources;
                    return of(this.sources);
                }
                return this.filterSources(inputValue);
            }),
            tap((data: any[]) => {
                this.filteredSources = data;
            }),
            catchError(() => of([]))
        ).subscribe();
    }

    normalizeString(value: string): string {
        return value.normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase();
    }

    loadDirectiiActive() {
        this.isLoading = true;
        this.commonService
            .getDirectiiActive()
            .pipe(
                map((results: any) => [...results]),
                finalize(() => this.isLoading = false)
            )
            .subscribe({
                next: (results) => {
                    this.directii = results;
                    this.filteredDirectii = [...this.directii];
                },
                error: (error) => {
                    console.error('Error loading directii', error);
                    this.filteredDirectii = [];
                },
            });
    }

    filterDirectii(searchTerm: string): Observable<any[]> {
        const normalizedSearchTerm = this.normalizeString(searchTerm);

        const filtered = this.directii.filter(directie =>
            this.normalizeString(directie.name).includes(normalizedSearchTerm)
        );

        return of(filtered);
    }

    onDirectieSelectClosed() {
        const selectedValue = this.directiiControl.value;

        if (selectedValue) {
            const isSelectedInFilteredList = this.filteredDirectii.some(directie =>
                directie.id === this.form.get('indoc_directie_id')?.value
            );

            if (!isSelectedInFilteredList) {
                this.directiiControl.setValue(null);
                this.filteredDirectii = this.directii;
                this.editDestroy$.next();
                this.editDestroy$.complete();
                this.renderFieldsSearch();
            }
        }
    }

    onDirectieChange(directieId: number): void {
        this.form.controls['indoc_compartiment_id'].patchValue(-1);
        const selectedDirectie = this.directii.find(directie => directie.id === directieId);

        if (selectedDirectie) {
            this.form.get('indoc_directie_name')?.patchValue(selectedDirectie.name);
        }

        if (directieId == -1) {
            this.compartimente = [];
            this.filteredCompartimente = [];
            return;
        }

        if (directieId) {
            this.loadCompartimenteActive(directieId);
        }
    }

    loadCompartimenteActive(directieId?: number) {
        this.isLoading = true;
        let compartimenteObservable$ = directieId
            ? this.commonService.getCompartimenteByDirectieId(directieId)
            : this.commonService.getCompartimenteActive();

        compartimenteObservable$.pipe(
            map((results: any) => [...results]),
            finalize(() => (this.isLoading = false))
        ).subscribe({
            next: (res) => {
                this.compartimente = [...res];
                this.filteredCompartimente = [...res];
            },
            error: (err) => {
                console.log(err);
                this.filteredCompartimente = [];
            },
        })
    }

    filterCompartimente(searchTerm: string): Observable<any[]> {
        const normalizedSearchTerm = this.normalizeString(searchTerm);

        const filtered = this.compartimente.filter(compartiment =>
            this.normalizeString(compartiment.name).includes(normalizedSearchTerm)
        );

        return of(filtered);
    }

    onCompartimentSelectClosed() {
        const selectedValue = this.compartimenteControl.value;

        if (selectedValue) {
            const isSelectedInFilteredList = this.filteredCompartimente.some(compartiment =>
                compartiment.id === this.form.get('indoc_compartiment_id')?.value
            );

            if (!isSelectedInFilteredList) {
                this.compartimenteControl.setValue(null);
                this.filteredCompartimente = this.compartimente;
                this.editDestroy$.next();
                this.editDestroy$.complete();
                this.renderFieldsSearch();
            }
        }
    }

    onCompartimentSelected(compartimentId: number) {
        const selectedCompartiment = this.compartimente.find(compartiment => compartiment.id === compartimentId);

        if (selectedCompartiment) {
            this.form.get('indoc_compartiment_name')?.patchValue(selectedCompartiment.name);
        }
    }

    loadRegistre() {
        this.isLoading = true;
        this.registreService
            .getIndocSerii()
            .pipe(
                map((results: any) => [...results.data]),
                finalize(() => this.isLoading = false)
            )
            .subscribe({
                next: (results) => {
                    this.registre = results;
                    this.filteredRegistre = [...this.registre];
                },
                error: (error) => {
                    console.error('Error loading registre', error);
                    this.filteredRegistre = [];
                },
            });
    }

    filterRegistre(searchTerm: string): Observable<any[]> {
        const normalizedSearchTerm = this.normalizeString(searchTerm);

        const filtered = this.registre.filter(registru =>
            this.normalizeString(registru.name).includes(normalizedSearchTerm)
        );

        return of(filtered);
    }

    onRegistreSelectClosed() {
        const selectedValue = this.registreControl.value;

        if (selectedValue) {
            const isSelectedInFilteredList = this.filteredRegistre.some(registru =>
                registru.id === this.form.get('indoc_registru_id')?.value
            );

            if (!isSelectedInFilteredList) {
                this.registreControl.setValue(null);
                this.filteredRegistre = this.registre;
                this.editDestroy$.next();
                this.editDestroy$.complete();
                this.renderFieldsSearch();
            }
        }
    }

    onRegistruSelected(registruId: number) {
        const selectedRegistru = this.registre.find(registru => registru.id === registruId);

        if (selectedRegistru) {
            this.form.get('indoc_registru_name')?.patchValue(selectedRegistru.name);
        }
    }

    loadCategorii() {
        this.isLoading = true;
        this.registreService
            .getCategorii()
            .pipe(
                map((results: any) => [...results.data]),
                finalize(() => this.isLoading = false)
            )
            .subscribe({
                next: (results) => {
                    this.categorii = results;
                },
                error: (error) => {
                    console.error('Error loading categorii', error);
                },
            });
    }

    onCategorieSelected(categorieId: number) {
        const selectedCategorie = this.categorii.find(categorie => categorie.id === categorieId);

        if (selectedCategorie) {
            this.form.get('indoc_categorie_name')?.patchValue(selectedCategorie.name);
        }
    }

    loadDocumente() {
        this.isLoading = true;
        this.registreService
            .getDocumenteTypes()
            .pipe(
                map((results: any) => [...results.data]),
                finalize(() => this.isLoading = false)
            )
            .subscribe({
                next: (results) => {
                    this.documente = results;
                    this.filteredDocumente = [...this.documente];
                },
                error: (error) => {
                    console.error('Error loading documente', error);
                    this.filteredDocumente = [];
                },
            });
    }

    filterDocumente(searchTerm: string): Observable<any[]> {
        const normalizedSearchTerm = this.normalizeString(searchTerm);

        const filtered = this.documente.filter(document =>
            this.normalizeString(document.name).includes(normalizedSearchTerm)
        );

        return of(filtered);
    }

    onDocumenteSelectClosed() {
        const selectedValue = this.documenteControl.value;

        if (selectedValue) {
            const isSelectedInFilteredList = this.filteredDocumente.some(document =>
                document.id === this.form.get('indoc_document_type_id')?.value
            );

            if (!isSelectedInFilteredList) {
                this.documenteControl.setValue(null);
                this.filteredDocumente = this.documente;
                this.editDestroy$.next();
                this.editDestroy$.complete();
                this.renderFieldsSearch();
            }
        }
    }

    onDocumentSelected(documentId: number): void {
        const selectedDocument = this.documente.find(document => document.id === documentId);

        if (selectedDocument) {
            this.form.get('indoc_document_type_name')?.patchValue(selectedDocument.name);
        }
    }

    loadSources() {
        this.isLoading = true;
        this.registreService
            .getSources()
            .pipe(
                map((results: any) => [...results.data]),
                finalize(() => this.isLoading = false)
            )
            .subscribe({
                next: (results) => {
                    this.sources = results;
                    this.filteredSources = [...this.sources];
                },
                error: (error) => {
                    console.error('Error loading sources', error);
                    this.filteredSources = [];
                },
            });
    }

    filterSources(searchTerm: string): Observable<any[]> {
        const normalizedSearchTerm = this.normalizeString(searchTerm);

        const filtered = this.sources.filter(source =>
            this.normalizeString(source.name).includes(normalizedSearchTerm)
        );

        return of(filtered);
    }

    onSourcesSelectClosed() {
        const selectedValue = this.sourcesControl.value;

        if (selectedValue) {
            const isSelectedInFilteredList = this.filteredSources.some(source =>
                source.id === this.form.get('indoc_source_id')?.value
            );

            if (!isSelectedInFilteredList) {
                this.sourcesControl.setValue(null);
                this.filteredSources = this.sources;
                this.editDestroy$.next();
                this.editDestroy$.complete();
                this.renderFieldsSearch();
            }
        }
    }

    onSourceSelected(sourceId: number) {
        const selectedSource = this.sources.find(source => source.id === sourceId);

        if (selectedSource) {
            this.form.get('indoc_source_name')?.patchValue(selectedSource.name);
        }
    }

    submitForm() {
        this.isLoading = true;

        const updatedRegistru = { id: this.data.id, ...this.form.value }

        this.registreService
            .updateRegistre(updatedRegistru)
            .pipe(finalize(() => (this.isLoading = false)))
            .subscribe({
                next: () => {
                    this.dialogRef.close({ success: true });
                },
                error: (err) => {
                    let msg;
                    try {
                        // try to catch the message from server
                        msg = err.error.errors.message[0];
                    } catch (error) {
                        // controll output message
                        msg = helper.dialogConfig.generalMessages.error;
                    }
                    this.notificationService.warningSwal(
                        this.errorTitle,
                        msg,
                        this.errorIcon
                    );
                },
            });
    }
}
